import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  PageLoading,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import IconLibrary from '../../../../components/IconLibrary';
import {
  getNotificationTypeDescription,
  getNotificationTypeLabel,
} from '../utils';
import {
  getPreferencesFromResult,
  mapFormValuesToPayload,
  mapPreferencesToFormValues,
  preferenceFieldName,
} from './utils';

function NotificationPreferencesForm({ apiPath }) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider = useDataProvider(
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default',
  );
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const requestIdRef = useRef(0);
  const { handleSubmit, register, reset, watch } = useForm({
    defaultValues: {},
  });
  const values = watch();

  const loadPreferences = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    setSaving(false);
    setLoadFailed(false);
    try {
      const result = await provider.get(apiPath);
      if (requestId !== requestIdRef.current) {
        return;
      }
      const loaded = getPreferencesFromResult(result);
      if (!result?.ok || loaded === null) {
        setLoadFailed(true);
        setPreferences([]);
        return;
      }
      setPreferences(loaded);
      reset(mapPreferencesToFormValues(loaded));
    } catch {
      if (requestId === requestIdRef.current) {
        setLoadFailed(true);
        setPreferences([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiPath, provider, reset]);

  useEffect(() => {
    loadPreferences();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadPreferences]);

  const onSubmit = async formValues => {
    if (!preferences.length) {
      return;
    }
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setSaving(true);
    try {
      const result = await provider.put(
        apiPath,
        mapFormValuesToPayload(preferences, formValues),
      );
      if (requestId !== requestIdRef.current) {
        return;
      }
      if (!result?.ok) {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={result?.errors} />,
        });
        return;
      }
      const saved = getPreferencesFromResult(result);
      if (saved) {
        setPreferences(saved);
        reset(mapPreferencesToFormValues(saved));
      }
      snackbar.showMessage(
        formatMessage({
          id: 'NOTIFICATION_PREFERENCES_SAVED',
          defaultMessage: 'Notification preferences saved',
        }),
      );
    } catch (error) {
      if (requestId === requestIdRef.current) {
        snackbar.showMessage({
          type: 'error',
          data: (
            <SnackbarApiErrorMessage
              errors={error?.result?.errors || error?.errors}
            />
          ),
        });
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return <PageLoading />;
  }
  if (loadFailed) {
    return (
      <Alert severity="error">
        {formatMessage({
          id: 'NOTIFICATION_PREFERENCES_COULD_NOT_BE_LOADED',
          defaultMessage: 'Notification preferences could not be loaded',
        })}
      </Alert>
    );
  }
  if (!preferences.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {formatMessage({
          id: 'THERE_ARE_NO_NOTIFICATIONS_TO_CONFIGURE',
          defaultMessage: 'There are no notifications to configure',
        })}
      </Typography>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              {formatMessage({
                id: 'NOTIFICATION',
                defaultMessage: 'Notification',
              })}
            </TableCell>
            <TableCell align="center">
              {formatMessage({
                id: 'EMAIL',
                defaultMessage: 'Email',
              })}
            </TableCell>
            <TableCell align="center">
              {formatMessage({
                id: 'IN_APP',
                defaultMessage: 'In-app',
              })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {preferences.map(preference => (
            <TableRow key={preference.typeCode}>
              <TableCell>
                <Typography variant="body2">
                  {getNotificationTypeLabel(formatMessage, preference)}
                </Typography>
                {preference.description && (
                  <Typography variant="caption" color="text.secondary">
                    {getNotificationTypeDescription(formatMessage, preference)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                <Checkbox
                  disabled={saving}
                  checked={
                    !!values[
                      preferenceFieldName(preference.typeCode, 'emailEnabled')
                    ]
                  }
                  inputProps={{
                    'aria-label': formatMessage(
                      {
                        id: 'CHANNEL_NOTIFICATIONS_FOR_TYPE',
                        defaultMessage: '{channel} notifications for {type}',
                      },
                      {
                        channel: formatMessage({
                          id: 'EMAIL',
                          defaultMessage: 'Email',
                        }),
                        type: getNotificationTypeLabel(
                          formatMessage,
                          preference,
                        ),
                      },
                    ),
                  }}
                  {...register(
                    preferenceFieldName(preference.typeCode, 'emailEnabled'),
                  )}
                />
              </TableCell>
              <TableCell align="center">
                <Tooltip
                  title={
                    preference.inAppMandatory
                      ? formatMessage({
                          id: 'NOTIFICATION_IN_APP_REQUIRED',
                          defaultMessage:
                            'Work assigned to you always appears here, so this cannot be switched off',
                        })
                      : ''
                  }>
                  <span>
                    <Checkbox
                      checked={
                        preference.inAppMandatory ||
                        !!values[
                          preferenceFieldName(
                            preference.typeCode,
                            'inAppEnabled',
                          )
                        ]
                      }
                      disabled={saving || preference.inAppMandatory}
                      inputProps={{
                        'aria-label': formatMessage(
                          {
                            id: 'CHANNEL_NOTIFICATIONS_FOR_TYPE',
                            defaultMessage:
                              '{channel} notifications for {type}',
                          },
                          {
                            channel: formatMessage({
                              id: 'IN_APP',
                              defaultMessage: 'In-app',
                            }),
                            type: getNotificationTypeLabel(
                              formatMessage,
                              preference,
                            ),
                          },
                        ),
                      }}
                      {...register(
                        preferenceFieldName(
                          preference.typeCode,
                          'inAppEnabled',
                        ),
                      )}
                    />
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={<IconLibrary.Save />}>
          {formatMessage({ id: 'SAVE', defaultMessage: 'Save' })}
        </Button>
      </Box>
    </form>
  );
}

export default NotificationPreferencesForm;
